import * as z from 'zod/mini';
import {
  RECORD_IDENTIFIER_MAX_LENGTH,
  RECORD_NAME_MAX_LENGTH,
  RECORD_NOTES_MAX_LENGTH,
} from '../constants.ts';
import { dateSchema, idSchema, timestampSchema } from './common.ts';
import { customValuesSchema } from './custom-type.ts';

// A record is a thing of the client's that is not a device: an SSL
// certificate, a domain, a software licence, a support contract, a warranty —
// or, since ROADMAP J7, a zone. It belongs to a client, carries when it
// expires and a note on how it is renewed, and the ones with an expiry feed
// two "Needs attention" rows — expiring within ATTENTION_RECORD_DAYS, and
// already expired — the same way a device's warranty does (K4). The expiry is
// optional, which is what lets a zone be one of these: a zone never lapses,
// it is simply the named area a security or AV device belongs to, and its
// devices name it in their own `zone` field.

/** What kind of thing the record is; the list drives the form's choice and the chip. */
export const RECORD_KINDS = [
  'ssl_certificate',
  'domain',
  'licence',
  'contract',
  'warranty',
  'zone',
  'other',
] as const;
export type RecordKind = (typeof RECORD_KINDS)[number];
export const recordKindSchema = z.enum(RECORD_KINDS);

export const RECORD_KIND_LABELS: Readonly<Record<RecordKind, string>> = {
  ssl_certificate: 'SSL certificate',
  domain: 'Domain',
  licence: 'Licence',
  contract: 'Contract',
  warranty: 'Warranty',
  zone: 'Zone',
  other: 'Other',
};

const recordNameSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'A name is needed'), z.maxLength(RECORD_NAME_MAX_LENGTH));
const recordIdentifierSchema = z
  .string()
  .check(z.trim(), z.maxLength(RECORD_IDENTIFIER_MAX_LENGTH));
const recordNotesSchema = z.string().check(z.maxLength(RECORD_NOTES_MAX_LENGTH));

export const recordSchema = z.object({
  id: idSchema,
  clientId: idSchema,
  propertyId: z.optional(z.nullable(idSchema)),
  customTypeId: z.optional(z.nullable(idSchema)),
  customValues: z.optional(customValuesSchema),
  kind: recordKindSchema,
  name: z.string(),
  /** The record's own identifier (a serial, a fingerprint, an order number); '' when none. */
  identifier: z.string(),
  /** When it expires, 'YYYY-MM-DD'. */
  expiresOn: z.string(),
  renewalNotes: z.string(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
// Named `ExpiringRecord`, not `Record`, so it never shadows the built-in
// `Record<K, V>` utility type this module (and its callers) rely on.
export type ExpiringRecord = z.infer<typeof recordSchema>;

const recordFields = {
  kind: recordKindSchema,
  name: recordNameSchema,
  identifier: recordIdentifierSchema,
  expiresOn: z.union([dateSchema, z.literal('')]),
  customTypeId: z.optional(z.nullable(idSchema)),
  customValues: z.optional(customValuesSchema),
  renewalNotes: recordNotesSchema,
};

export const recordInputSchema = z.object({
  ...recordFields,
  identifier: z._default(recordFields.identifier, ''),
  renewalNotes: z._default(recordFields.renewalNotes, ''),
});
export type RecordInput = z.infer<typeof recordInputSchema>;

export const recordPatchSchema = z.partial(z.object(recordFields));
export type RecordPatch = z.infer<typeof recordPatchSchema>;

export const recordEnvelopeSchema = z.object({ record: recordSchema });
export const recordListSchema = z.object({
  /** The soonest to expire first. */
  records: z.array(recordSchema),
});

/** Field key → the label its form shows; the history names edits by these. */
export const RECORD_FIELD_LABELS: Readonly<Record<keyof RecordInput, string>> = {
  kind: 'Kind',
  name: 'Name',
  identifier: 'Identifier',
  expiresOn: 'Expiry',
  renewalNotes: 'Renewal notes',
  customTypeId: 'Custom asset type',
  customValues: 'Custom fields',
};

/** Whether an expiry has passed, is within the horizon, or is comfortably off. */
export type RecordExpiry = 'expired' | 'soon' | 'ok';

/** The state of one record's expiry, judged against today and the horizon (both 'YYYY-MM-DD'). */
export function recordExpiry(expiresOn: string, today: string, horizon: string): RecordExpiry {
  if (expiresOn === '') return 'ok';
  if (expiresOn < today) {
    return 'expired';
  }
  return expiresOn <= horizon ? 'soon' : 'ok';
}
